import { Component, type ReactNode, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

const Login = lazy(() => import("./pages/Login"));
const Crm = lazy(() => import("./pages/Crm"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingKitPortaProntaV1 = lazy(() => import("./pages/LandingKitPortaPronta").then((m) => ({ default: m.LandingKitPortaProntaV1 })));
const LandingKitPortaProntaV2 = lazy(() => import("./pages/LandingKitPortaPronta").then((m) => ({ default: m.LandingKitPortaProntaV2 })));
const LandingKitPortaProntaV3 = lazy(() => import("./pages/LandingKitPortaPronta").then((m) => ({ default: m.LandingKitPortaProntaV3 })));

const queryClient = new QueryClient();

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; message: string }
> {
  state = {
    hasError: false,
    message: "",
  };

  static getDerivedStateFromError(error: unknown) {
    return {
      hasError: true,
      message:
        error instanceof Error
          ? error.message
          : "Erro inesperado ao carregar a página.",
    };
  }

  componentDidCatch(error: unknown) {
    console.error("[AppErrorBoundary]", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
          <div className="max-w-lg rounded-xl border border-border bg-card p-6 shadow-sm">
            <h1 className="font-heading text-xl font-semibold text-foreground">
              Erro ao carregar a página
            </h1>

            <p className="mt-3 text-sm text-muted-foreground">
              Atualize a página. Se continuar, limpe o cache ou abra em aba
              anônima.
            </p>

            <p className="mt-3 break-words rounded-md bg-muted p-3 text-xs text-muted-foreground">
              {this.state.message}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
    Carregando...
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />

        <BrowserRouter>
          <AppErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} />
                <Route path="/crm" element={<Crm />} />
                <Route path="/obrigado" element={<ThankYou />} />
                <Route path="/lp/v1/kit-porta-pronta" element={<LandingKitPortaProntaV1 />} />
                <Route path="/lp/v2/kit-porta-pronta" element={<LandingKitPortaProntaV2 />} />
                <Route path="/lp/v3/kit-porta-pronta" element={<LandingKitPortaProntaV3 />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;